import { request } from './http';
import mockProductService from '../mocks/mockProductService';

const API_BASE = process.env.REACT_APP_API_URL || '';
const USE_MOCK = !API_BASE || API_BASE === '';

export async function fetchProductFilters(storeId) {
  if (USE_MOCK) {
    return mockProductService.fetchProductFilters();
  }
  try {
    const url = `${API_BASE}/api/products/filters${storeId ? `?store=${storeId}` : ''}`;
    const res = await request(url, { method: 'GET' });
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  } catch (err) {
    console.warn('API failed, using mock:', err);
    return mockProductService.fetchProductFilters();
  }
}

export async function searchProducts(params = {}) {
  if (USE_MOCK) {
    return mockProductService.searchProducts(params);
  }
  try {
    const query = Object.keys(params).map(k => `${encodeURIComponent(k)}=${encodeURIComponent(params[k])}`).join('&');
    const url = `${API_BASE}/api/products${query ? '?' + query : ''}`;
    const res = await request(url, { method: 'GET' });
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  } catch (err) {
    console.warn('API failed, using mock:', err);
    return mockProductService.searchProducts(params);
  }
}

export async function fetchProductById(id) {
  if (USE_MOCK) {
    return mockProductService.fetchProductById(id);
  }
  try {
    const url = `${API_BASE}/api/products/${id}`;
    const res = await request(url, { method: 'GET' });
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  } catch (err) {
    console.warn('API failed, using mock:', err);
    return mockProductService.fetchProductById(id);
  }
}

export async function fetchCNAEs() {
  if (USE_MOCK) {
    return mockProductService.fetchCNAEs();
  }
  try {
    const url = `${API_BASE}/api/account/cnaes`;
    const res = await request(url, { method: 'GET' });
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  } catch (err) {
    console.warn('API failed, using mock:', err);
    return mockProductService.fetchCNAEs();
  }
}

export async function createProduct(payload) {
  if (USE_MOCK) {
    return mockProductService.createProduct(payload);
  }
  try {
    const url = `${API_BASE}/api/products`;
    // payload may contain files; if so, build FormData
    if (payload.file instanceof File) {
      const form = new FormData();
      Object.keys(payload).forEach(k => {
        if (k === 'file') return;
        if (payload[k] !== undefined && payload[k] !== null) form.append(k, payload[k]);
      });
      form.append('image', payload.file);
      const res = await request(url, { method: 'POST', body: form });
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    }
    const res = await request(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  } catch (err) {
    console.warn('API failed, using mock:', err);
    return mockProductService.createProduct(payload);
  }
}

export async function updateProduct(id, payload) {
  if (USE_MOCK) {
    return mockProductService.updateProduct(id, payload);
  }
  try {
    const url = `${API_BASE}/api/products/${id}`;
    const res = await request(url, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  } catch (err) {
    console.warn('API failed, using mock:', err);
    return mockProductService.updateProduct(id, payload);
  }
}

export async function deleteProduct(id) {
  if (USE_MOCK) {
    return mockProductService.deleteProduct(id);
  }
  try {
    const url = `${API_BASE}/api/products/${id}`;
    const res = await request(url, { method: 'DELETE' });
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  } catch (err) {
    console.warn('API failed, using mock:', err);
    return mockProductService.deleteProduct(id);
  }
}

export async function createVariant(productId, payload) {
  if (USE_MOCK) {
    // Mock doesn't have separate variant creation (variants created with product)
    return { success: false, error: 'Not implemented in mock' };
  }
  try {
    const url = `${API_BASE}/api/products/${productId}/variants`;
    const res = await request(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  } catch (err) { throw err; }
}

export const fetchDocuments = async (page, perPage) => {
  if (USE_MOCK) {
    return mockProductService.fetchDocuments(page, perPage);
  }
  try {
    const response = await fetch(`${API_BASE}/api/documents?page=${page || 1}&perPage=${perPage || 5}`);
    const data = await response.json();
    return data;
  } catch (error) {
    console.warn('API failed, using mock:', error);
    return mockProductService.fetchDocuments(page, perPage);
  }
};

export const searchDocuments = async (searchTerm) => {
  if (USE_MOCK) {
    return mockProductService.searchDocuments(searchTerm);
  }
  try {
    const response = await fetch(`${API_BASE}/api/documents/search?query=${searchTerm}`);
    const data = await response.json();
    return data;
  } catch (error) {
    console.warn('API failed, using mock:', error);
    return mockProductService.searchDocuments(searchTerm);
  }
};

const productService = { 
  fetchProductFilters, 
  searchProducts, 
  fetchProductById,
  fetchCNAEs, 
  createProduct, 
  updateProduct,
  deleteProduct,
  createVariant, 
  fetchDocuments, 
  searchDocuments 
};

export default productService;